import { type PropsWithChildren, type ReactNode, type FC } from 'react'
import classNames from 'classnames'
import { Typography, type TextStylesType } from 'shared/ui/Typography'
import { Icon, type IIconsVector } from 'shared/ui/Icon'
import { Collapse } from '../Collapse'
import styles from './styles.module.scss'

type ICollapseWithIconProps = {
  isOpen?: boolean
  title: ReactNode
  subtitle?: ReactNode
  label?: ReactNode
  icon: IIconsVector
  type?: 'withHover' | 'init'
  titleVariant?: TextStylesType
  subtitleVariant?: TextStylesType
  labelVariant?: TextStylesType
  onClick?: (opened: boolean) => void
  opened?: boolean
  className?: string
  headerClassName?: string
  contentClassName?: string
  variant?: 'default' | 'active'
  error?: string
}

export const CollapseWithIcon: FC<PropsWithChildren<ICollapseWithIconProps>> = ({
  isOpen = false,
  title,
  subtitle,
  label,
  titleVariant = 'body-md-medium',
  subtitleVariant = 'body-md-medium',
  labelVariant = 'body-md-regular',
  type = 'withHover',
  icon,
  children,
  onClick,
  opened,
  className,
  headerClassName,
  contentClassName,
  variant,
  error,
}) => {
  const isActiveStyle = opened && variant === 'active'

  return (
    <Collapse
      className={className}
      opened={opened}
      onClick={onClick}
      isOpen={isOpen}
      leftContent={() => (
        <div
          className={classNames(styles.iconWrapper, {
            [styles.isActiveIcon]: isActiveStyle,
          })}
        >
          <Icon
            fontSize={16}
            icon={icon}
            color={isActiveStyle ? 'var(--white-100)' : 'var(--content-primary-tertiary)'}
          />
        </div>
      )}
      title={
        <div className={styles.main}>
          <div className={styles.header}>
            <Typography ariaLabel={'title'} variant={titleVariant}>
              {title}
            </Typography>
            {subtitle &&
              (typeof subtitle === 'string' ? (
                <Typography ariaLabel={'subtitle'} variant={subtitleVariant} color={'tertiary'}>
                  {subtitle}
                </Typography>
              ) : (
                subtitle
              ))}
          </div>
          {label && (
            <Typography
              ariaLabel={'label'}
              variant={labelVariant}
              color={isActiveStyle ? 'var(--light-blue-100)' : 'tertiary'}
            >
              {label}
            </Typography>
          )}
        </div>
      }
      type={type}
      headerClassName={headerClassName}
      variant={variant}
      error={error}
    >
      <div className={classNames(styles.content, contentClassName)}>{children}</div>
    </Collapse>
  )
}
